import { useMemo } from 'react';
import {
  qAppConfig,
  getRegionalSettings,
  formatMoney as formatMoneyRaw,
  type AppConfigRow,
  type RegionalSettings,
} from '@helios/data-access';
import type { Money } from '@helios/domain';
import { usePowerSyncQuery } from '../adapters/powersync';

/**
 * Value types accepted by the formatter returned from `useFormatMoney`.
 * - `Money` (Dinero) — converted via `.toRoundedUnit(2)`
 * - number / numeric string — interpreted as major units
 * - null / undefined — formatted as zero
 */
export type MoneyInput = Money | number | string | null | undefined;

interface DineroLike {
  toRoundedUnit: (precision: number) => number;
}

function isDinero(v: unknown): v is DineroLike {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { toRoundedUnit?: unknown }).toRoundedUnit === 'function'
  );
}

function toMajorNumber(value: MoneyInput): number {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const n = parseFloat(value.replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  if (isDinero(value)) return value.toRoundedUnit(2);
  return 0;
}

/**
 * Reactive read of the singleton app_config row's regional_settings.
 * Falls back to PH defaults when the row or extra_json is missing/malformed.
 */
export function useRegionalSettings(): RegionalSettings {
  const { data } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  return useMemo(() => getRegionalSettings(data?.[0] ?? null), [data]);
}

export interface UseFormatMoneyOptions {
  /** Force decimal_places to 0 — useful for compact chips. */
  compact?: boolean;
}

/**
 * Returns a memoized formatter function bound to the clinic's current
 * regional money_format settings. The formatter accepts Dinero `Money`,
 * a major-unit number, a numeric string, or null/undefined.
 */
export function useFormatMoney(options?: UseFormatMoneyOptions): (value: MoneyInput) => string {
  const regional = useRegionalSettings();
  const compact = options?.compact ?? false;
  return useMemo(() => {
    const effective: RegionalSettings = compact
      ? { ...regional, money_format: { ...regional.money_format, decimal_places: 0 } }
      : regional;
    return (value: MoneyInput) => formatMoneyRaw(toMajorNumber(value), effective);
  }, [regional, compact]);
}
